/** Pure helpers for the Lumana pages — no network, safe to unit test. */
import { PERMISSIONS } from "../../Constants/permission";

/**
 * While true, Lumana is Quickvee-support only in the panel: the Connector menu
 * item, the page and the order footage strip show for superadmin sessions only.
 * UI only — the Node API (router/lumana_router.js) already admits every session
 * type by permission, so going live is this one flag: owners see everything,
 * employees see what their permissions allow (canManageLumana / canViewOrderVideo).
 */
// Flipped to false 2026-10-21: live for every store. Set true to return to the superadmin pilot.
export const LUMANA_SUPERADMIN_ONLY = false;

const OWNER_LOGIN_TYPES = ["merchant", "admin", "superadmin"];

const ownerOrCode = (loginType, permissions, code) =>
  OWNER_LOGIN_TYPES.includes(String(loginType || "")) || (Array.isArray(permissions) && permissions.includes(code));

/** Connector page + menu: owners always; an employee with "Access Lumana Video". */
export const canManageLumana = (loginType, permissions = []) =>
  ownerOrCode(loginType, permissions, PERMISSIONS.MENU_LUMANA_CONNECTOR);

/** Order footage: owners always; an employee with "View Camera Footage". */
export const canViewOrderVideo = (loginType, permissions = []) =>
  ownerOrCode(loginType, permissions, PERMISSIONS.MENU_VIEW_CAMERA_FOOTAGE);

const pilot = (loginType) => String(loginType || "") === "superadmin";

/**
 * Connector menu item + page: superadmin while the pilot flag is on, otherwise
 * owners and employees holding "Access Lumana Video". (The 2026-10-21 pre-live
 * merge had left this as `() => true`, showing the menu to every employee.)
 */
export const lumanaVisibleFor = (loginType, permissions) =>
  LUMANA_SUPERADMIN_ONLY ? pilot(loginType) : canManageLumana(loginType, permissions);

/** Order footage strip: superadmin while the pilot flag is on, otherwise by "View Camera Footage". */
export const lumanaVideoVisibleFor = (loginType, permissions) =>
  LUMANA_SUPERADMIN_ONLY ? pilot(loginType) : canViewOrderVideo(loginType, permissions);

/** Server messages are written for merchants; fall back only when absent. */
export const readError = (err, fallback = "Something went wrong. Try again.") =>
  err?.response?.data?.error?.message || err?.response?.data?.message || fallback;

export const errorCode = (err) => err?.response?.data?.error?.code || "";

/** "2:05:10 PM" in the store's own zone (falls back to the browser's). */
export const fmtClock = (ms, timeZone) => {
  if (!Number.isFinite(ms)) return "";
  try {
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      timeZone: timeZone || undefined,
    }).format(new Date(ms));
  } catch {
    return new Date(ms).toLocaleTimeString("en-US");
  }
};

/** "Oct 7, 2:05 PM" in the store's zone. */
export const fmtStamp = (ms, timeZone) => {
  if (!Number.isFinite(ms)) return "";
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: timeZone || undefined,
    }).format(new Date(ms));
  } catch {
    return new Date(ms).toLocaleString("en-US");
  }
};

/**
 * Who acted on a moment: a refund names the employee who refunded it (never
 * the sale's cashier); the sale falls back to the order's cashier.
 */
export const momentCashier = (moment, order) => {
  const own = String(moment?.cashier ?? "").trim();
  if (own) return own;
  return moment?.type === "SALE" ? String(order?.cashier ?? "").trim() : "";
};

export const fmtMoney = (n) =>
  Number.isFinite(Number(n))
    ? Number(n).toLocaleString("en-US", { style: "currency", currency: "USD" })
    : "";

/** Whole days from today to a YYYY-MM-DD date; null when absent. */
export const daysUntil = (day, now = new Date()) => {
  if (!day || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const [y, m, d] = day.split("-").map(Number);
  const end = Date.UTC(y, m - 1, d);
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((end - today) / 86400000);
};

export const EVENT_LABEL = {
  SALE: "Sale",
  REFUND: "Refund",
  CASH_DROP: "Cash drop",
  PAY_IN: "Pay-in",
  TEST: "Test",
};

/** Where to place the event marker on a clip, as a 0–1 fraction. */
export const markerFraction = (eventAtMs, startMs, endMs) => {
  const span = Number(endMs) - Number(startMs);
  if (!(span > 0)) return 0;
  return Math.min(1, Math.max(0, (Number(eventAtMs) - Number(startMs)) / span));
};

/** Lumana's own names for the tag field types, as its "Create event tag" form shows them. */
export const LUMANA_TYPE_LABEL = { text: "Text", decimal: "Decimal", boolean: "True/False" };

/**
 * What to say about the event tag after discovery: Quickvee finds or creates it
 * in the merchant's Lumana organization, so the merchant only reads this.
 */
export const tagState = (discovery) => {
  if (!discovery) return null;
  if (discovery.suggested_event_type_id) {
    return {
      tone: "ok",
      text: discovery.event_tag_created
        ? "Created in your Lumana organization just now."
        : "Found in your Lumana organization.",
    };
  }
  if (discovery.event_tags_error) {
    return {
      tone: "warn",
      text: `Lumana did not let Quickvee create or list the tag (${discovery.event_tags_error}). Create it by hand with the fields above, then pick it or enter its id.`,
    };
  }
  return { tone: "warn", text: "Quickvee could not find or create the tag. Reconnect, or create it by hand with the fields above." };
};

export const GUIDE_STEP_COUNT = 8;

/**
 * Which setup-guide steps this store has finished, read from the saved state
 * the status call returns (never from unsaved edits on the page).
 *   1-2 Org ID + API key, 4 connect   → a connection exists
 *   3 tag created in Lumana           → a tag is saved, or Lumana lists ours
 *   5 tag chosen                      → event_type_id saved
 *   6 cameras                         → at least one camera mapped
 *   7 events saved                    → tag + cameras + at least one event type
 *   8 tested                          → Lumana accepted a real event
 */
export const guideProgress = (status, discovery) => {
  const conn = status?.connected ? status.connection || {} : null;
  const tagSaved = Boolean(conn?.event_type_id);
  const mapped = Array.isArray(status?.cameras) && status.cameras.length > 0;
  const done = {
    1: Boolean(conn),
    2: Boolean(conn),
    3: tagSaved || Boolean(conn && discovery?.suggested_event_type_id),
    4: Boolean(conn),
    5: tagSaved,
    6: mapped,
    7: mapped && tagSaved && (conn?.enabled_events || []).length > 0,
    8: Boolean(conn?.last_sent_at) || Number(status?.stats?.sent) > 0,
  };
  let next = null;
  for (let n = 1; n <= GUIDE_STEP_COUNT; n += 1) {
    if (!done[n]) {
      next = n;
      break;
    }
  }
  return { done, next, count: Object.values(done).filter(Boolean).length };
};
